import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const ThemeContext = createContext();

const VALID_MODES = ['light', 'dark', 'system'];
const STORAGE_KEY = 'theme';

// Read the cached preference from localStorage so the first paint matches the
// last session (no flash). AuthContext reconciles this to the SERVER value
// (fn_get_my_profile → theme_preference) once the profile loads, via setTheme().
const readStoredMode = () => {
  try {
    const m = localStorage.getItem(STORAGE_KEY);
    if (m && VALID_MODES.includes(m)) return m;
  } catch { /* localStorage unavailable */ }
  return 'system';
};

const prefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // themeMode is the *preference*: 'light' | 'dark' | 'system'. Seeded from
  // localStorage (no-flash) and reconciled to the server value by AuthContext.
  const [themeMode, setThemeMode] = useState(readStoredMode);

  // Track the OS color-scheme so 'system' re-evaluates live when the OS changes.
  const [systemDark, setSystemDark] = useState(prefersDark);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemDark(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler); // older Safari
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);

  // Derive the *applied* dark state from the preference + OS preference.
  const isDarkMode =
    themeMode === 'dark' || (themeMode === 'system' && systemDark);

  // Apply to the DOM + mirror the preference to localStorage. Mirroring
  // themeMode (not isDarkMode) means 'system' survives a reload rather than
  // collapsing to light/dark.
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) root.classList.add('dark');
    else root.classList.remove('dark');
    try { localStorage.setItem(STORAGE_KEY, themeMode); } catch { /* ignore */ }
  }, [isDarkMode, themeMode]);

  // Light↔dark toggle for the existing binary call sites (header buttons,
  // login / reset-password pages). 'system' is selectable via setTheme() from
  // the profile page; toggleTheme never lands on 'system'.
  const toggleTheme = useCallback(() => {
    setThemeMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  // Set the preference from a known-good value (the server via AuthContext, or
  // the profile page's 3-way control). Invalid values fall back to 'system'.
  const setTheme = useCallback((mode) => {
    setThemeMode(VALID_MODES.includes(mode) ? mode : 'system');
  }, []);

  // Memoize so consumers don't re-render on unrelated provider renders. Keys
  // consumed today: isDarkMode (~15 components), toggleTheme (header/login),
  // themeMode + setTheme (profile page, AuthContext). All stable unless the
  // underlying values change.
  const value = useMemo(
    () => ({ isDarkMode, themeMode, toggleTheme, setTheme }),
    [isDarkMode, themeMode, toggleTheme, setTheme]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

